import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import type { VertexSetWithVertices, SharedVertexSetInfo, VertexSetUnionWithTarget, PublicVertexSetMetadata } from '../../types';
import * as api from '../../services/api';
import { DeleteIcon, UnionIcon } from '../icons';

interface UnionsPanelProps {
  currentSetId: string;
  currentShareId: string | null;
  allUserSets: VertexSetWithVertices[];
  onError: (message: string) => void;
  onSelectionChange: (selectedUnions: VertexSetUnionWithTarget[]) => void;
  onUnionsLoaded?: (unions: VertexSetUnionWithTarget[]) => void;
}

export function UnionsPanel({ currentSetId, currentShareId, allUserSets, onError, onSelectionChange, onUnionsLoaded }: UnionsPanelProps) {
  const [unions, setUnions] = useState<VertexSetUnionWithTarget[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [externalSetInfo, setExternalSetInfo] = useState<SharedVertexSetInfo | null>(null);
  const [isLoadingExternal, setIsLoadingExternal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Alias selection state
  const [selectedAlias, setSelectedAlias] = useState<string | null>(null);
  const [aliasSearchResults, setAliasSearchResults] = useState<string[]>([]);
  const [aliasVertexSets, setAliasVertexSets] = useState<PublicVertexSetMetadata[]>([]);
  const [isLoadingAliases, setIsLoadingAliases] = useState(false);
  const [isLoadingAliasSets, setIsLoadingAliasSets] = useState(false);

  // Keyboard navigation
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Load unions when currentSetId changes
  useEffect(() => {
    loadUnions();
  }, [currentSetId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function loadUnions() {
    setLoading(true);
    try {
      const unionsList = await api.fetchVertexSetUnions(currentSetId);

      // Fetch target info for each union
      const unionsWithTargets: VertexSetUnionWithTarget[] = [];
      for (const union of unionsList) {
        try {
          const target = await api.fetchSharedVertexSetInfo(union.targetShareId);
          unionsWithTargets.push({ union, target });
        } catch (_) {
          // Skip unions where target is no longer accessible
        }
      }

      // Sort by createdAt descending (newest first)
      unionsWithTargets.sort((a, b) =>
        new Date(b.union.createdAt).getTime() - new Date(a.union.createdAt).getTime()
      );

      setUnions(unionsWithTargets);
      onUnionsLoaded?.(unionsWithTargets);
    } catch (err) {
      console.error('Failed to load unions:', err);
    } finally {
      setLoading(false);
    }
  }

  // Filter user's own shared sets (exclude current set and already unioned sets)
  const existingUnionIds = new Set(unions.map((u) => u.union.targetShareId));
  const ownSharedSets = allUserSets.filter(
    (s) => s.shareId && s.vertexSet.id !== currentSetId && !existingUnionIds.has(s.shareId)
  );

  // Alias search mode detection
  const isAliasSearchMode = searchQuery.startsWith('@') && !selectedAlias;
  const aliasQuery = isAliasSearchMode ? searchQuery.slice(1) : '';

  // Filter by search query (only in normal mode)
  const filteredOwnSets = (!isAliasSearchMode && !selectedAlias)
    ? ownSharedSets.filter((s) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
          s.vertexSet.name.toLowerCase().includes(q) ||
          (s.vertexSet.description?.toLowerCase().includes(q)) ||
          s.shareId?.toLowerCase().includes(q)
        );
      })
    : [];

  // Filtered alias sets (when alias is selected)
  const filteredAliasSets = selectedAlias
    ? aliasVertexSets.filter((s) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
          s.vertexSet.name.toLowerCase().includes(q) ||
          s.vertexSet.description?.toLowerCase().includes(q)
        );
      })
    : [];

  // Fetch alias search results
  useEffect(() => {
    if (isAliasSearchMode && aliasQuery.length > 0) {
      const timer = setTimeout(async () => {
        setIsLoadingAliases(true);
        try {
          const aliases = await api.searchUserAliases(aliasQuery, 10);
          setAliasSearchResults(aliases);
        } catch (_) {
          setAliasSearchResults([]);
        }
        setIsLoadingAliases(false);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setAliasSearchResults([]);
    }
  }, [searchQuery, selectedAlias]);

  // Fetch sets when alias is selected
  useEffect(() => {
    if (selectedAlias) {
      setIsLoadingAliasSets(true);
      api.fetchPublicVertexSetsByAlias(selectedAlias)
        .then((sets) => {
          const available = sets.filter(
            (s) => s.shareId !== currentShareId && !existingUnionIds.has(s.shareId)
          );
          setAliasVertexSets(available);
        })
        .catch(() => setAliasVertexSets([]))
        .finally(() => setIsLoadingAliasSets(false));
    } else {
      setAliasVertexSets([]);
    }
  }, [selectedAlias, currentShareId, unions]);

  // Fetch external set info when no local matches (only in normal mode)
  useEffect(() => {
    if (!isAliasSearchMode && !selectedAlias && searchQuery && filteredOwnSets.length === 0) {
      const timer = setTimeout(async () => {
        setIsLoadingExternal(true);
        try {
          const info = await api.fetchSharedVertexSetInfo(searchQuery);
          setExternalSetInfo(info);
        } catch (_) {
          setExternalSetInfo(null);
        }
        setIsLoadingExternal(false);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setExternalSetInfo(null);
      setIsLoadingExternal(false);
    }
  }, [searchQuery, filteredOwnSets.length, isAliasSearchMode, selectedAlias]);

  // Reset highlighted index when dropdown items change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [aliasSearchResults, filteredAliasSets, filteredOwnSets, externalSetInfo]);

  async function handleSelectSet(targetShareId: string) {
    if (currentShareId && targetShareId === currentShareId) {
      onError('Cannot create a union with the same set');
      return;
    }

    try {
      await api.createVertexSetUnion(currentSetId, targetShareId);
      await loadUnions();
      setSearchQuery('');
      setSelectedAlias(null);
      setIsDropdownOpen(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to create union');
    }
  }

  async function handleRemoveUnion(targetShareId: string) {
    try {
      await api.deleteVertexSetUnion(currentSetId, targetShareId);
      const updatedUnions = unions.filter((u) => u.union.targetShareId !== targetShareId);
      setUnions(updatedUnions);
      onUnionsLoaded?.(updatedUnions);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to remove union');
    }
  }

  // Get current dropdown items for keyboard navigation
  function getDropdownItems(): Array<{ type: 'alias' | 'set'; value: string }> {
    if (isAliasSearchMode) {
      return aliasSearchResults.map((alias) => ({ type: 'alias', value: alias }));
    }
    if (selectedAlias) {
      return filteredAliasSets.map((s) => ({ type: 'set', value: s.shareId }));
    }
    // Normal mode
    const items: Array<{ type: 'alias' | 'set'; value: string }> = [];
    for (const s of filteredOwnSets) {
      if (s.shareId) items.push({ type: 'set', value: s.shareId });
    }
    if (filteredOwnSets.length === 0 && externalSetInfo && searchQuery !== currentShareId) {
      items.push({ type: 'set', value: searchQuery });
    }
    return items;
  }

  function handleKeyDown(e: KeyboardEvent) {
    const items = getDropdownItems();

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && items.length > 0) {
      e.preventDefault();
      const item = items[highlightedIndex];
      if (item) {
        if (item.type === 'alias') {
          setSelectedAlias(item.value);
          setSearchQuery('');
        } else {
          handleSelectSet(item.value);
        }
      }
    } else if (e.ctrlKey && e.key === 'Enter' && searchQuery) {
      handleSelectSet(searchQuery);
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      inputRef.current?.blur();
    } else if (e.key === 'Backspace' && selectedAlias && searchQuery === '') {
      e.preventDefault();
      setSelectedAlias(null);
    }
  }

  function toggleUnionSelection(targetShareId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(targetShareId)) {
        next.delete(targetShareId);
      } else {
        next.add(targetShareId);
      }
      // Notify parent of selection change
      const selectedUnions = unions.filter((u) => next.has(u.union.targetShareId));
      onSelectionChange(selectedUnions);
      return next;
    });
  }

  return (
    <div class="unions-panel">
      <h3><UnionIcon size={20} /> Unions</h3>

      {loading ? (
        <p class="unions-loading">Loading...</p>
      ) : (
        <div class="unions-list">
          {/* Add union input */}
          <div class="union-add-container" ref={containerRef}>
            {selectedAlias && (
              <span class="union-alias-pill">
                @{selectedAlias}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAlias(null);
                    setSearchQuery('');
                  }}
                  title="Clear"
                >
                  ×
                </button>
              </span>
            )}
            <input
              ref={inputRef}
              type="text"
              class={selectedAlias ? 'union-add-input union-add-input-with-pill' : 'union-add-input'}
              placeholder={selectedAlias ? 'Filter sets...' : 'Add union...'}
              value={searchQuery}
              onInput={(e) => setSearchQuery(e.currentTarget.value)}
              onFocus={() => setIsDropdownOpen(true)}
              onKeyDown={handleKeyDown}
            />
            {isDropdownOpen && (searchQuery || filteredOwnSets.length > 0 || isLoadingExternal || selectedAlias) && (
              <div class="union-autocomplete">
                {/* Phase 1: Alias search results */}
                {isAliasSearchMode && (
                  <>
                    {aliasSearchResults.map((alias, idx) => (
                      <div
                        key={alias}
                        class={idx === highlightedIndex ? 'union-autocomplete-item union-autocomplete-highlighted' : 'union-autocomplete-item'}
                        onClick={() => {
                          setSelectedAlias(alias);
                          setSearchQuery('');
                        }}
                      >
                        <span class="union-autocomplete-name">@{alias}</span>
                      </div>
                    ))}
                    {isLoadingAliases && (
                      <div class="union-autocomplete-loading">Loading...</div>
                    )}
                    {!isLoadingAliases && aliasSearchResults.length === 0 && aliasQuery && (
                      <div class="union-autocomplete-empty">No matching users.</div>
                    )}
                  </>
                )}

                {/* Phase 2: Sets from selected alias */}
                {selectedAlias && (
                  <>
                    {filteredAliasSets.map((s, idx) => (
                      <div
                        key={s.shareId}
                        class={idx === highlightedIndex ? 'union-autocomplete-item union-autocomplete-external union-autocomplete-highlighted' : 'union-autocomplete-item union-autocomplete-external'}
                        onClick={() => handleSelectSet(s.shareId)}
                      >
                        <span class="union-autocomplete-name">{s.vertexSet.name}</span>
                        <span class="union-autocomplete-id">{s.shareId}</span>
                      </div>
                    ))}
                    {isLoadingAliasSets && (
                      <div class="union-autocomplete-loading">Loading sets...</div>
                    )}
                    {!isLoadingAliasSets && filteredAliasSets.length === 0 && (
                      <div class="union-autocomplete-empty">
                        {aliasVertexSets.length === 0 ? 'No public sets from this user.' : 'No matching sets.'}
                      </div>
                    )}
                  </>
                )}

                {/* Normal mode: own sets and external lookup */}
                {!isAliasSearchMode && !selectedAlias && (
                  <>
                    {/* User's own filtered sets */}
                    {filteredOwnSets.map((s, idx) => (
                      <div
                        key={s.shareId}
                        class={idx === highlightedIndex ? 'union-autocomplete-item union-autocomplete-highlighted' : 'union-autocomplete-item'}
                        onClick={() => handleSelectSet(s.shareId!)}
                      >
                        <span class="union-autocomplete-name">{s.vertexSet.name}</span>
                        <span class="union-autocomplete-id">{s.shareId}</span>
                      </div>
                    ))}

                    {/* External set (if no local matches and fetched, and not current set) */}
                    {filteredOwnSets.length === 0 && externalSetInfo && searchQuery !== currentShareId && (
                      <div
                        class={highlightedIndex === 0 ? 'union-autocomplete-item union-autocomplete-external union-autocomplete-highlighted' : 'union-autocomplete-item union-autocomplete-external'}
                        onClick={() => handleSelectSet(searchQuery)}
                      >
                        <span class="union-autocomplete-name">{externalSetInfo.name}</span>
                        {externalSetInfo.ownerAlias && (
                          <span class="union-autocomplete-owner">
                            by {externalSetInfo.ownerAlias}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Current set's shareId pasted */}
                    {filteredOwnSets.length === 0 && searchQuery === currentShareId && (
                      <div class="union-autocomplete-empty">
                        Cannot union a set with itself.
                      </div>
                    )}

                    {/* Loading indicator */}
                    {isLoadingExternal && (
                      <div class="union-autocomplete-loading">Loading...</div>
                    )}

                    {/* No results message */}
                    {filteredOwnSets.length === 0 && !externalSetInfo && !isLoadingExternal && searchQuery && searchQuery !== currentShareId && (
                      <div class="union-autocomplete-empty">
                        No matching sets. Press Ctrl+Enter to try as shareId.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Union items */}
          {unions.map(({ union, target }) => {
            const isSelected = selectedIds.has(union.targetShareId);
            return (
              <div
                key={union.targetShareId}
                class={isSelected ? 'union-item union-item-selected' : 'union-item'}
                onClick={() => toggleUnionSelection(union.targetShareId)}
              >
                <span class="union-item-name">
                  {target.name}
                </span>
                {target.ownerAlias && (
                  <span class="union-owner">by {target.ownerAlias}</span>
                )}
                {target.description && (
                  <p class="union-description">{target.description}</p>
                )}
                <button
                  class="union-remove-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveUnion(union.targetShareId);
                  }}
                  title="Remove union"
                >
<DeleteIcon size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
